// WHERE EACH PART OF A MULTIPART ENVIRONMENT ENDS UP IN THE WORLD.
//
// A part carries a LOCAL transform (`environmentParts.js`: world = envPose ∘
// partPose), because the environment's own `default_pose` is the registration of
// the whole capture into world space — re-registering the room must not mean
// rewriting every part.
//
// THE COMPOSITION HAS TO HAPPEN SOMEWHERE, AND IT MOVED. The component that drew
// the environment before three did put every part inside ONE posed group, which
// is what a mesh carrying its own transform needs. three's `GaussianSplat` is
// drawn with its placement BAKED INTO ITS ATTRIBUTES — the same space the
// avatar's own bridge uses, so the two cannot drift, and the bridge has no choice
// because it shares one mesh with the avatar. A baked mesh has no transform left
// to inherit from a parent, so the two poses are composed here instead and each
// part is baked at its world placement.
//
// IT USES THREE'S OWN MATRIX, and that is worth stating because the first version
// did not: it hand-rolled the 3x3 compose, multiply and euler decomposition on
// the grounds that a module importing THREE could not run under `node --test`.
// That is simply false — `shFrameRotation.js`, `bakeLayout.js`, `skinWeights.js`
// and six others import it and are covered by node:test. The hand-rolled version
// was ~60 lines including a degenerate-euler branch, and its own test used
// `Object3D.matrixWorld` as the reference, which is the tell: if the reference is
// available to the test it is available to the module.
//
// Delegating also removes the part most likely to be quietly wrong. `Euler`'s
// gimbal case at ry = ±90° is not exotic here — it is a wall — and three's
// `setFromRotationMatrix` already handles it, in the same way the scene graph did
// when it owned this composition.

import * as THREE from 'three'

/** @typedef {{position: number[], rotation: number[], scale: number}} Pose */

// Module-level scratch. `placeParts` runs per environment change rather than per
// frame, so this is not a hot path — but a room is up to MAX_PARTS mounts and
// allocating six objects per part for no reason is still noise.
const _env = new THREE.Matrix4()
const _part = new THREE.Matrix4()
const _t = new THREE.Vector3()
const _q = new THREE.Quaternion()
const _s = new THREE.Vector3()
const _e = new THREE.Euler()
const _v = new THREE.Vector3()

/** A `{position, rotation, scale}` pose into a TRS matrix, in `out`. */
function toMatrix(out, pose) {
  const p = pose?.position ?? [0, 0, 0]
  const r = pose?.rotation ?? [0, 0, 0]
  const s = pose?.scale ?? 1
  _e.set(r[0], r[1], r[2], 'XYZ')
  return out.compose(_v.set(p[0], p[1], p[2]), _q.setFromEuler(_e), _s.set(s, s, s))
}

/**
 * Compose the environment's pose with one part's local pose.
 *
 * SCALE IS UNIFORM on both sides (a part carries one `scale`, and so does the
 * row), so the product is uniform too and the decompose cannot produce a shear —
 * which is what makes an euler+scalar an honest representation of the result
 * rather than a lossy one.
 *
 * @param {Pose} env   the environment row's own pose
 * @param {Pose} part  the part's local pose
 * @returns {Pose}     the part's world placement
 */
export function composePartPose(env, part) {
  toMatrix(_env, env)
  toMatrix(_part, part)
  _part.premultiply(_env).decompose(_t, _q, _s)
  _e.setFromQuaternion(_q, 'XYZ')
  return {
    position: [_t.x, _t.y, _t.z],
    rotation: [_e.x, _e.y, _e.z],
    scale: _s.x,
  }
}

/**
 * Every mount from `partMounts`, placed in the world.
 *
 * @param {Array<{position: number[], rotation: number[], scale: number}>} mounts
 * @param {Pose} env
 */
export function placeParts(mounts, env) {
  return mounts.map((m) => ({ ...m, world: composePartPose(env, m) }))
}
